import React, { useState, useEffect } from "react";
import { useClientInfo } from "../../utils/commonImports";
import CategoryDropdown from "../../components/CategoryDropdown";
import MyDatePicker from "../../components/MyDatePicker";
import { convertDate } from "../../utils/dateUtils";
import { SnackbarAlert } from "../../utils/commonImports";

// ================================================================
// GST SETTINGS FORM
// ================================================================
const GstSettingsForm = ({ onSave }) => {
  const { clientId } = useClientInfo();

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    registered_for_gst: true,

    gstin: "",
    registration_type: "",

    business_legal_name: "",
    business_trade_name: "",

    gst_registered_on: "",
    reverse_charge: false,

    tax_rate_cgst: "",
    tax_rate_sgst: "",
    tax_rate_igst: "",

    place_of_supply: "",
    service_category: "",
    hsn_code: "",

    gst_notification: "",
  });

  // ==============================================================
  // FETCH GST SETTINGS
  // ==============================================================
  useEffect(() => {
    const fetchGST = async () => {
      try {
        const res = await fetch(
          `/api/clients/${clientId}/gst-settings`
        );

        const data = await res.json();

        if (data && data.gst) {
          setForm({
            registered_for_gst:
              data.gst.registered_for_gst ?? true,

            gstin:
              data.gst.gstin || "",

            registration_type:
              data.gst.registration_type || "",

            business_legal_name:
              data.gst.business_legal_name || "",

            business_trade_name:
              data.gst.business_trade_name || "",

            gst_registered_on:
              data.gst.gst_registered_on || "",

            reverse_charge:
              data.gst.reverse_charge || false,

            tax_rate_cgst:
              data.gst.tax_rate_cgst || "",

            tax_rate_sgst:
              data.gst.tax_rate_sgst || "",

            tax_rate_igst:
              data.gst.tax_rate_igst || "",

            place_of_supply:
              data.gst.place_of_supply || "",

            service_category:
              data.gst.service_category || "",

            hsn_code:
              data.gst.hsn_code || "",

            gst_notification:
              data.gst.gst_notification || "",
          });
        }
      } catch (err) {
        console.error(
          "Error loading GST settings:",
          err
        );
      }

      setLoading(false);
    };

    fetchGST();
  }, [clientId]);

  // ==============================================================
  // HANDLE INPUT CHANGE
  // ==============================================================
  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  // ==============================================================
  // GST TOGGLE
  // ==============================================================
  const handleToggleGST = () => {
    setForm((prev) => ({
      ...prev,
      registered_for_gst:
        !prev.registered_for_gst,
    }));
  };

  // ==============================================================
  // REGISTRATION TYPE
  // ==============================================================
  const handleRegistrationTypeChange = (e) => {
    setForm((prev) => ({
      ...prev,
      registration_type:
        e.target.label,
    }));
  };

  // ==============================================================
  // PLACE OF SUPPLY
  // ==============================================================
  const handlePlaceOfSupplyChange = (e) => {
    setForm((prev) => ({
      ...prev,
      place_of_supply:
        e.target.label,
    }));
  };

  // ==============================================================
  // SERVICE CATEGORY + AUTO HSN
  // ==============================================================
  const handleServiceCategoryChange = (e) => {
    const selectedService =
      e.target.label;

    // ------------------------------------------------------------
    // SERVICE → HSN MAPPING
    // ------------------------------------------------------------
    const SERVICE_HSN_MAP = {
      "Rent A Cab": "996412",
      "Tour Operator": "998555",
      "Tour Operater": "998555",
      "Car Rentals": "996412",
      "Car Rental": "996412",
      "Car Rentals (Self Drive)": "996601",
    };

    const autoHSN =
      SERVICE_HSN_MAP[selectedService] || "";

    setForm((prev) => ({
      ...prev,
      service_category:
        selectedService,
      hsn_code:
        autoHSN,
    }));
  };

  // ==============================================================
  // SUBMIT
  // ==============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...form,

        gst_registered_on:
          convertDate(
            form.gst_registered_on
          ),
      };

      const res = await fetch(
        `/api/clients/${clientId}/gst-settings`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      SnackbarAlert(
        data.message ||
          "GST settings saved!",
        "success"
      );

      if (onSave) {
        onSave(payload);
      }
    } catch (err) {
      console.error(
        "GST SAVE ERROR:",
        err
      );

      if (err.response) {
        console.error(
          "Backend Response:",
          err.response
        );
      }

      SnackbarAlert(
        "Error saving GST settings",
        "error"
      );
    }
  };

  // ==============================================================
  // LOADING
  // ==============================================================
  if (loading) {
    return (
      <div className="text-center mt-5 text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  // ==============================================================
  // UI
  // ==============================================================
  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-5xl bg-white rounded-lg shadow-md my-6 ml-0 overflow-hidden"
    >

      {/* ========================================================
          HEADER
      ========================================================= */}
      <div className="px-6 py-4 border-b border-gray-200">

        <h2 className="text-xl font-semibold text-gray-900">
          GST Settings
        </h2>

        <p className="text-xs text-gray-400 mt-0.5">
          Configure GST registration, tax rates and
          service-related tax information.
        </p>

      </div>

      {/* ========================================================
          FORM CONTENT
      ========================================================= */}
      <div className="px-6 py-5">

        {/* ======================================================
            01 GST REGISTRATION
        ======================================================= */}
        <SectionTitle
          number="01"
          title="GST Registration"
        />

        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-4 py-2.5">

          <div>

            <div className="text-sm font-medium text-gray-700">
              Is your business registered for GST?
            </div>

            <div className="text-xs text-gray-400 mt-0.5">
              Enable GST settings if your organization
              is registered under GST.
            </div>

          </div>

          <div className="flex items-center gap-2">

            <button
              type="button"
              onClick={handleToggleGST}
              className={`
                relative
                w-11
                h-6
                rounded-full
                transition-colors
                focus:outline-none
                ${
                  form.registered_for_gst
                    ? "bg-blue-600"
                    : "bg-gray-300"
                }
              `}
            >

              <span
                className={`
                  absolute
                  top-0.5
                  left-0.5
                  w-5
                  h-5
                  bg-white
                  rounded-full
                  shadow
                  transition-transform
                  ${
                    form.registered_for_gst
                      ? "translate-x-5"
                      : "translate-x-0"
                  }
                `}
              />

            </button>

            <span
              className={`
                text-sm font-medium
                ${
                  form.registered_for_gst
                    ? "text-blue-600"
                    : "text-gray-500"
                }
              `}
            >
              {form.registered_for_gst
                ? "Yes"
                : "No"}
            </span>

          </div>

        </div>

        {/* ======================================================
            GST DETAILS
        ======================================================= */}
        {form.registered_for_gst && (
          <>

            {/* ==================================================
                02 BASIC GST INFORMATION
            =================================================== */}
            <SectionTitle
              number="02"
              title="GST Information"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">

              {/* GSTIN */}
              <Field
                label="GSTIN"
                required
              >

                <div className="flex gap-2">

                  <input
                    name="gstin"
                    maxLength={15}
                    className={inputClass}
                    placeholder="15 character GSTIN"
                    value={form.gstin}
                    onChange={handleChange}
                    required
                  />

                  <button
                    type="button"
                    className="px-3 whitespace-nowrap text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                  >
                    Get Details
                  </button>

                </div>

              </Field>

              {/* Registration Type */}
              <Field label="Registration Type">

                <CategoryDropdown
                  clientId={clientId}
                  categoryType="GST"
                  name="registration_type"
                  value={form.registration_type}
                  onChange={
                    handleRegistrationTypeChange
                  }
                  label="Registration Type"
                  size="small"
                />

              </Field>

              {/* Legal Name */}
              <Field label="Business Legal Name">

                <input
                  name="business_legal_name"
                  className={inputClass}
                  placeholder="Legal business name"
                  value={
                    form.business_legal_name
                  }
                  onChange={handleChange}
                />

              </Field>

              {/* Trade Name */}
              <Field label="Business Trade Name">

                <input
                  name="business_trade_name"
                  className={inputClass}
                  placeholder="Trade / brand name"
                  value={
                    form.business_trade_name
                  }
                  onChange={handleChange}
                />

              </Field>

              {/* GST Registered On */}
              <Field label="GST Registered On">

                <MyDatePicker
                  label="GST Registered On"
                  value={
                    form.gst_registered_on
                  }
                  onChange={(date) =>
                    setForm((prev) => ({
                      ...prev,
                      gst_registered_on:
                        date
                          ? new Date(date)
                          : null,
                    }))
                  }
                />

              </Field>

              {/* Reverse Charge */}
              <Field label="Reverse Charge">

                <div className="h-9 flex items-center">

                  <label className="flex items-center gap-2 cursor-pointer">

                    <input
                      type="checkbox"
                      name="reverse_charge"
                      checked={
                        form.reverse_charge
                      }
                      onChange={
                        handleChange
                      }
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />

                    <span className="text-sm text-gray-700">
                      Reverse Charge Applicable
                    </span>

                  </label>

                </div>

              </Field>

            </div>

            {/* ==================================================
                03 TAX RATES
            =================================================== */}
            <SectionTitle
              number="03"
              title="Tax Rates"
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              <Field label="CGST (%)">

                <div className="relative">

                  <input
                    name="tax_rate_cgst"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`${inputClass} pr-8`}
                    placeholder="0.00"
                    value={
                      form.tax_rate_cgst
                    }
                    onChange={handleChange}
                  />

                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    %
                  </span>

                </div>

              </Field>

              <Field label="SGST (%)">

                <div className="relative">

                  <input
                    name="tax_rate_sgst"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`${inputClass} pr-8`}
                    placeholder="0.00"
                    value={
                      form.tax_rate_sgst
                    }
                    onChange={handleChange}
                  />

                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    %
                  </span>

                </div>

              </Field>

              <Field label="IGST (%)">

                <div className="relative">

                  <input
                    name="tax_rate_igst"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`${inputClass} pr-8`}
                    placeholder="0.00"
                    value={
                      form.tax_rate_igst
                    }
                    onChange={handleChange}
                  />

                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    %
                  </span>

                </div>

              </Field>

            </div>

            {/* ==================================================
                04 SERVICE & TAX CLASSIFICATION
            =================================================== */}
            <SectionTitle
              number="04"
              title="Service & Tax Classification"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-3">

              {/* Place of Supply */}
              <Field label="Place of Supply">

                <CategoryDropdown
                  clientId={clientId}
                  categoryType="State"
                  name="place_of_supply"
                  value={
                    form.place_of_supply
                  }
                  onChange={
                    handlePlaceOfSupplyChange
                  }
                  label="Place of Supply"
                  size="small"
                />

              </Field>

              {/* Service Category */}
              <Field label="Service Category">

                <CategoryDropdown
                  clientId={clientId}
                  categoryType="Service"
                  name="service_category"
                  value={
                    form.service_category
                  }
                  onChange={
                    handleServiceCategoryChange
                  }
                  label="Service Category"
                  size="small"
                />

              </Field>

              {/* HSN */}
              <Field label="HSN Code">

                <input
                  name="hsn_code"
                  className={inputClass}
                  placeholder="HSN / SAC Code"
                  value={
                    form.hsn_code || ""
                  }
                  onChange={handleChange}
                />

              </Field>

            </div>

            {/* ==================================================
                05 GST NOTIFICATION
            =================================================== */}
            <SectionTitle
              number="05"
              title="GST Notification"
            />

            <Field label="GST Notification">

              <textarea
                name="gst_notification"
                className="w-full min-h-[70px] rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 placeholder:text-gray-300 resize-y"
                rows={2}
                placeholder="Enter GST notification or additional tax information"
                value={
                  form.gst_notification
                }
                onChange={handleChange}
              />

            </Field>

          </>
        )}

        {/* ======================================================
            SAVE
        ======================================================= */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">

          <p className="text-xs text-gray-400">

            {form.registered_for_gst ? (
              <>
                <span className="text-red-500">
                  *
                </span>{" "}
                GSTIN is required
              </>
            ) : (
              "GST registration is disabled"
            )}

          </p>

          <button
            type="submit"
            className="px-8 py-2.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
          >
            Save Changes
          </button>

        </div>

      </div>
    </form>
  );
};

// ================================================================
// SECTION TITLE
// ================================================================
const SectionTitle = ({
  number,
  title,
}) => {
  return (
    <div className="mt-5 mb-3 pb-1.5 border-b border-gray-200">

      <div className="flex items-center gap-2">

        <span className="flex items-center justify-center w-6 h-6 rounded bg-blue-50 text-blue-600 text-[10px] font-semibold">
          {number}
        </span>

        <h3 className="text-sm font-semibold text-gray-800">
          {title}
        </h3>

      </div>

    </div>
  );
};

// ================================================================
// FIELD
// ================================================================
const Field = ({
  label,
  required = false,
  children,
}) => {
  return (
    <div className="min-w-0">

      <label className="block text-xs font-medium text-gray-500 mb-1">

        {label}

        {required && (
          <span className="text-red-500 ml-1">
            *
          </span>
        )}

      </label>

      {children}

    </div>
  );
};

// ================================================================
// COMMON INPUT STYLE
// ================================================================
const inputClass =
  "w-full h-9 rounded border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 placeholder:text-gray-300";

export default GstSettingsForm;

